import React, { useCallback, useEffect, useMemo, useState } from "react";
import DailyListTable from "./DailyListTable";
import ModalItem from "./ModalItem";
import { useNotification } from "./NotificationContext";
import useWsEvents from "../hooks/useWsEvents";
import { buildProcessedMeters, getItemChanges, mapDatabaseItems, normalizeItemDateForApi } from "../utils/items";
import { getCorrectOrder, getDatabase, getVoorraad, setProp, updateDailyListNode } from "../api/wsApi";

function MeteredItemsPage({
    itemFilter,
    newDateRetries = 0,
}) {
    const [data, setData] = useState({});
    const [correctOrder, setCorrectOrder] = useState([]);
    const [voorraad, setVoorraad] = useState({ stock: {}, created: null });

    const [selectedItem, setSelectedItem] = useState(null);
    const [showModal, setShowModal] = useState(false);

    const notification = useNotification();

    const fetchCorrectOrderData = useCallback(() => {
        getCorrectOrder()
            .then((data) => setCorrectOrder(data))
            .catch((err) => {
                console.log("Error fetching correct order", err);
            });
    }, []);

    const fetchDatabaseData = useCallback(() => {
        getDatabase()
            .then((data) => {
                setData(mapDatabaseItems(data));
            })
            .catch((err) => {
                console.log("Error fetching daily list (database)", err);
            });
    }, []);

    const fetchVoorraadData = useCallback(() => {
        getVoorraad()
            .then((data) => setVoorraad(data))
            .catch((err) => {
                console.log("Error fetching voorraad", err);
            });
    }, []);

    useEffect(() => {
        fetchCorrectOrderData();
        fetchDatabaseData();
        fetchVoorraadData();
    }, [fetchCorrectOrderData, fetchDatabaseData, fetchVoorraadData]);

    useWsEvents((message) => {
        switch (message.event) {
            case "databaseUpdated":
                fetchDatabaseData();
                break;
            case "correctOrderUpdated":
                fetchCorrectOrderData();
                break;
            case "voorraadUpdated":
                fetchVoorraadData();
                break;
            default:
                break;
        }
    });

    const processedData = useMemo(
        () =>
            buildProcessedMeters(correctOrder, data, (item) =>
                itemFilter ? itemFilter(item, voorraad) : true
            ),
        [correctOrder, data, itemFilter, voorraad]
    );

    const openBeheer = useCallback(
        (id) => {
            setSelectedItem(data[id]);
            setShowModal(true);
        },
        [data]
    );

    React.useEffect(() => {
        if (selectedItem && data[selectedItem.id]) {
            setSelectedItem(data[selectedItem.id]);
        }
    }, [data]);

    const newDate = useCallback(
        (id, date, retryCount = 0) => {
            const normalizedDate = normalizeItemDateForApi(date);
            updateDailyListNode(id, normalizedDate)
                .then(() => {
                    fetchDatabaseData();
                })
                .catch((err) => {
                    console.log("Error updating date", err);
                    if (retryCount < newDateRetries) {
                        setTimeout(() => {
                            newDate(id, date, retryCount + 1);
                        }, 1000);
                    } else if (newDateRetries > 0) {
                        notification("Error updating date", "error");
                    }
                });
        },
        [fetchDatabaseData, newDateRetries, notification]
    );

    const updateDataItem = useCallback(
        (item) => {
            const originalItem = data[item.id];
            const changes = getItemChanges(item, originalItem);
            if (changes.length === 0) {
                return;
            }
            const requests = changes.map(({ key, value }) => setProp(key, item.id, value));
            Promise.all(requests)
                .then(() => {
                    fetchDatabaseData();
                    fetchCorrectOrderData();
                })
                .then(() => {
                    notification("Item updated", "success");
                })
                .catch((err) => {
                    console.log("Error updating item", err);
                    notification("Error updating item", "error");
                });
        },
        [data, fetchCorrectOrderData, fetchDatabaseData, notification]
    );

    return (
        <div>
            <ul>
                {processedData && (
                    <DailyListTable
                        meters={processedData}
                        openBeheer={openBeheer}
                        quickAction={() => {
                            alert("Quick action does not work yet");
                        }}
                        newDate={newDate}
                        voorraad={voorraad.stock}
                    />
                )}
            </ul>

            {showModal && (
                <ModalItem
                    item={selectedItem}
                    onClick={() => {
                        setShowModal(false);
                    }}
                    updateDataItem={updateDataItem}
                    fetchData={fetchDatabaseData}
                />
            )}
        </div>
    );
}

export default MeteredItemsPage;
